import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Mic2, Search, Play, Pause, Edit, Download, Lock, Trash2, CheckCircle,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { formatTime } from '../audio/audioCodec';

// List of saved recordings. Each row has a multitrack-selection
// checkbox, title + duration + tags, and a row of action buttons
// (play / edit / download / delete). Renders three empty states:
//  - no recordings at all
//  - recordings exist but no match for the current filters
//  - the list of rows

const RecordingsList = ({
  recordings,
  filteredRecordings,
  selectedTrackIds,
  playingId,
  loadingPlayId,
  downloadingId,
  isPremium,
  onToggleSelection,
  onPlay,
  onEdit,
  onDownload,
  onDelete,
}) => {
  const { t } = useTranslation();

  if (filteredRecordings.length === 0 && recordings.length === 0) {
    return (
      <div className="bg-[#121212] border border-white/5 rounded-2xl p-12 text-center">
        <Mic2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
        <p className="text-zinc-400">{t('recorder.noRecordings')}</p>
      </div>
    );
  }

  if (filteredRecordings.length === 0) {
    return (
      <div className="bg-[#121212] border border-white/5 rounded-2xl p-8 text-center">
        <Search className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
        <p className="text-zinc-400">Nessuna registrazione trovata con i filtri attuali</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filteredRecordings.map((rec, index) => (
        <motion.div
          key={rec.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center justify-between hover:border-amber/30 transition-colors"
        >
          <div className="flex items-center gap-4">
            <button
              onClick={(e) => { e.stopPropagation(); onToggleSelection(rec.id); }}
              className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                selectedTrackIds.has(rec.id)
                  ? 'bg-purple-500 border-purple-500'
                  : 'border-zinc-600 hover:border-zinc-400'
              }`}
              data-testid={`track-select-${rec.id}`}
            >
              {selectedTrackIds.has(rec.id) && (
                <CheckCircle className="w-4 h-4 text-white" />
              )}
            </button>
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Mic2 className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h4 className="font-semibold text-white">{rec.title}</h4>
              <div className="flex items-center gap-2 text-zinc-500 text-sm">
                <span>{formatTime(Math.round(rec.duration_seconds))}</span>
                {rec.bpm && <span>• {rec.bpm} BPM</span>}
              </div>
              {rec.tags && rec.tags.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {rec.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                      {tag}
                    </span>
                  ))}
                  {rec.tags.length > 3 && (
                    <span className="text-xs text-zinc-500">+{rec.tags.length - 3}</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPlay(rec)}
              disabled={loadingPlayId === rec.id}
              className={`${
                playingId === rec.id
                  ? 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                  : 'text-amber hover:text-amber/80 hover:bg-amber/10'
              }`}
              title={playingId === rec.id ? 'Stop' : 'Play'}
              data-testid={`play-btn-${rec.id}`}
            >
              {loadingPlayId === rec.id ? (
                <div className="w-4 h-4 border-2 border-amber border-t-transparent rounded-full animate-spin" />
              ) : playingId === rec.id ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(rec)}
              className="text-blue hover:text-blue/80 hover:bg-blue/10"
              title={t('recorder.editTitle')}
              data-testid={`edit-btn-${rec.id}`}
            >
              <Edit className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDownload(rec)}
              disabled={!isPremium || downloadingId === rec.id}
              className={`${
                isPremium
                  ? 'text-blue hover:text-blue/80 hover:bg-blue/10'
                  : 'text-zinc-600 cursor-not-allowed'
              }`}
              title={isPremium ? t('recorder.downloadWav') : t('recorder.proOnly')}
              data-testid={`download-btn-${rec.id}`}
            >
              {downloadingId === rec.id ? (
                <div className="w-4 h-4 border-2 border-blue border-t-transparent rounded-full animate-spin" />
              ) : isPremium ? (
                <Download className="w-4 h-4" />
              ) : (
                <Lock className="w-4 h-4" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(rec.id)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid={`delete-btn-${rec.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default RecordingsList;
